import type {
  OperationalStatusEnum,
  SecondaryStatusEnum,
  SecondaryStatusRoboticMowerEnum,
} from 'fleet-data-model';
import paths from '~/app/paths/paths';
import { getValuesFromQueryString } from '~/utils/query-helper/query-helper';
import formatMessageWrapper from '~/utils/format-message-wrapper/format-message-wrapper';
import { FiltersValuesMap, GroupedQueryFilter } from '../models/list-filter-types';

export const getValueLabel = (
  type: string,
  value: string,
  mapOperationalStatus: (status: OperationalStatusEnum) => string,
  mapSecondaryStatus: (status: SecondaryStatusEnum) => string
) => {
  switch (type) {
    case 'asset_type':
      return formatMessageWrapper(`bff.web.asset_type.${value.toLowerCase()}`);
    case 'robotic_mower_status':
      return formatMessageWrapper(`web.robotic_mower_status.${value}`);
    case 'inventory_status':
      return formatMessageWrapper(`bff.inventory_status.${value.toLowerCase()}`);
    case 'subcategory':
      return formatMessageWrapper(`pim.subcategory.${value.toLowerCase()}`);
    case 'firmware_update_approved_state':
      return formatMessageWrapper(
        `bff.dashboard.firmware_card.approved_state.${value.toLowerCase()}`
      );
    case 'maintenance_reminder_status':
      return formatMessageWrapper(`bff.dashboard.maintenance_card.${value.toLowerCase()}`);
    case 'maintenance_status':
      return formatMessageWrapper(`bff.dashboard.maintenance_card.${value.toLowerCase()}`);
    case 'online_status':
      return formatMessageWrapper(
        `bff.dashboard.direct_connect_gateways.online_state.${
          value === 'ONLINE' ? 'true' : 'false'
        }`
      );
    case 'operational_status':
      return mapOperationalStatus(value as OperationalStatusEnum);
    case 'secondary_status':
      return mapSecondaryStatus(value as SecondaryStatusRoboticMowerEnum);
    case 'last_seen':
      return formatMessageWrapper('web.status.dashboard.last_seen.filter_pill.label', {
        value: formatMessageWrapper(`web.status.dashboard.last_seen.filter_pill.value.${value}`),
      });
  }
  return value;
};

export const clearFilterValue = (
  filters: FiltersValuesMap,
  type: string,
  value: string
): FiltersValuesMap => {
  if (filters?.[type]?.includes(value)) {
    const updatedFilters = {
      ...filters,
    };
    updatedFilters[type] = filters[type].filter((v) => v !== value);
    if (updatedFilters[type].length === 0) {
      delete updatedFilters[type];
    }
    return updatedFilters;
  }
  return filters;
};

export const clearFilter = (filters: FiltersValuesMap, type: string): FiltersValuesMap => {
  if (filters?.[type]) {
    const updatedFilters = {
      ...filters,
    };
    delete updatedFilters[type];
    return updatedFilters;
  }
  return filters;
};

export const getQueryFilters = (url: string | undefined): FiltersValuesMap => {
  if (!url) {
    return {};
  }
  const matches = url.match(/\?.*/);
  if (matches?.[0] === undefined) {
    return {};
  }

  const params = new URLSearchParams(matches[0]);
  const filters = params.get('filters');

  if (!filters) {
    return {};
  }
  const parsedFilters = JSON.parse(filters);
  if (!parsedFilters || parsedFilters.length === 0) {
    return {};
  }

  return parsedFilters;
};

export const groupQueryFilters = (filters: FiltersValuesMap): GroupedQueryFilter[] =>
  Object.keys(filters)
    .filter((key) => filters[key].length > 0)
    .map((key) => ({
      code: key,
      values: filters[key],
    })) ?? [];

export const getLimitedAssetListLink = (assetId?: string, includeFilters?: boolean) => ({
  search: includeFilters ? getSearchFilters() : '',
  pathname: paths.inventory.getPath(assetId ? { machineId: assetId } : undefined),
});

export const getSearchFilters = () => {
  const queryValues = getValuesFromQueryString(window.location.search, ['filters']);
  return queryValues?.filters ? `filters=${encodeFiltersParam(queryValues?.filters)}` : '';
};

export const fetchFiltersValuesFromQuery = () => getQueryFilters(window.location.href);

/**
 * Encode a filters JSON string for use in the URL query string.
 * Keeps structural characters ({, }, [, ], ", :, ,) and enum values readable,
 * while escaping characters that would break query-string parsing (& # + % space).
 * Old bookmarks using full encodeURIComponent still parse because
 * URLSearchParams.get() decodes both forms.
 */
export const encodeFiltersParam = (json: string): string =>
  json.replace(/[&#+ %]/g, (ch) => encodeURIComponent(ch));

export const exportFilters = (filtersValues: FiltersValuesMap) => {
  const groups = groupQueryFilters(filtersValues);
  const result = groups.reduce((acc, filterRecord) => {
    const filterKey = filterRecord.code;
    acc[`filter[${filterKey}]`] = acc[`filter[${filterKey}]`] || [];
    acc[`filter[${filterKey}]`].push(
      Array.isArray(filterRecord.values) ? filterRecord.values.join(',') : filterRecord.values
    );
    return acc;
  }, {});
  return result;
};
